import {
  Breakpoints,
  CTA,
  Container,
  ContainerPadding,
  Layout,
  LayoutConfiguration,
  Text,
  TokenTextAppearance,
  styled,
} from '@volkswagen-onehub/components-core';
import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { CarlineContext, useTrackingManager } from 'src/feature-app/hooks';
import { Offer } from 'src/types';
import { css } from 'styled-components';
import parse from 'html-react-parser';
import { getMarketingCode } from 'src/feature-app';

type OfferProps = {
  showCard: boolean;
};

const OfferCampaignContainer = styled.div<OfferProps>`
  width: 100%;
  height: 100%;
  opacity: ${(props) => (props.showCard ? '1' : '0')};
  ${(props) =>
    !props.showCard &&
    css`
      &.offer-enter {
        opacity: 0;
      }
      &.offer-enter-active {
        opacity: 1;
        transition: opacity 400ms ease-in;
      }
      &.offer-enter-done {
        opacity: 1;
      }
      &.offer-exit {
        opacity: 1;
      }
      &.offer-exit-active {
        opacity: 0.01;
        transition: opacity 1ms;
      }
    `};
`;

const StyledImageWrapper = styled.div`
  width: 100%;
  min-height: 70px;
  margin-top: 30px;
  position: relative;
  .car-image {
    width: 100%;
  }

  .logo-campana {
    position: absolute;
    max-width: 30%;
    max-height: 75px;
  }

  @media (min-width: 560px) {
    margin-top: 0px;
  }
`;

const MotorizacionContainer = styled.div`
  & div {
    margin: 0px;
  }

  min-height: 48px;
`;

const CarlineModelCuotaInfoContainer = styled.div`
  min-height: 100%;
  display: flex;
  flex-wrap: always;
  flex-direction: column;
  justify-content: space-between;
`;

const InfoContainer = styled.div``;

interface CarlineOfferProps {
  offer: Offer;
  offerNumber?: number;
  activeComparador: boolean;
  numberOfOffers: number;
  offerChange?: boolean;
  showCard: boolean;
  setSelectedOffer?: React.Dispatch<React.SetStateAction<Offer>>;
  setShowOfferToFalse?: () => void;
  unSelectCarline?: () => void;
  setShowLead?: React.Dispatch<React.SetStateAction<boolean>>;
}

const moreThanOneOffer: LayoutConfiguration = {
  [Breakpoints.default]: [
    { name: 'a', columns: 24 },
    { name: 'b', columns: 24 },
    { name: 'c', columns: 24 },
  ],
};

const oneOffer: LayoutConfiguration = {
  [Breakpoints.default]: [
    { name: '.', columns: 2 },
    { name: 'a', columns: 20 },
    { name: '.', columns: 2 },
    { name: '.', columns: 2 },
    { name: 'b', columns: 20 },
    { name: '.', columns: 2 },
    { name: '.', columns: 2 },
    { name: 'c', columns: 20 },
    { name: '.', columns: 2 },
  ],
  [Breakpoints.b560]: [
    { name: '.', columns: 4 },
    { name: 'a', columns: 16 },
    { name: '.', columns: 4 },
    { name: '.', columns: 4 },
    { name: 'b', columns: 16 },
    { name: '.', columns: 4 },
    { name: '.', columns: 4 },
    { name: 'c', columns: 16 },
    { name: '.', columns: 4 },
  ],
  [Breakpoints.b960]: [
    { name: '.', columns: 2 },
    { name: 'a', columns: 6 },
    { name: '.', columns: 1 },
    { name: 'b', columns: 6 },
    { name: '.', columns: 1 },
    { name: 'c', columns: 6 },
    { name: '.', columns: 2 },
  ],
};

export const CarlineOfferCampaign = (props: CarlineOfferProps) => {
  const marketingCodeConfigRef = useRef(null);
  const trackingManager = useTrackingManager();

  const carline = useContext(CarlineContext);
  const { offer, offerNumber, numberOfOffers, offerChange, showCard } = props;

  const [showCarline, setShowCarline] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (!offerChange) setShowCarline(false);
  }, [offerChange]);

  useLayoutEffect(() => {
    if (offerNumber !== undefined) {
      const offerTimeout = 500 + 400 * offerNumber;
      setTimeout(() => setShowCarline(true), offerTimeout);
    } else {
      setTimeout(() => setShowCarline(true), 500);
    }
  }, [carline]);

  const trackCTAClick = async (text: string, carlineName: string, campaignName: string, url: string): Promise<void> => {
    const { marketingCodeConfig } = await getMarketingCode(offer.marketingCode);
    marketingCodeConfigRef.current = marketingCodeConfig;

    trackingManager.trackOffersVehicleButtonClick(carline, { contentId: text }, marketingCodeConfig, {
      name: `ofertas:${carlineName}:${campaignName}`,
      url: url,
    });
  };

  return (
    <CSSTransition timeout={400} in={showCarline} classNames="offer">
      <OfferCampaignContainer id={`${carline.id}`} showCard={showCard}>
        <Container>
          <Layout appearance={numberOfOffers === 1 ? oneOffer : moreThanOneOffer}>
            <div style={{ position: 'relative' }}>
              <div className="test-1 etiqueta-vacia" style={{ height: '30px' }}></div>

              <StyledImageWrapper className="styled-image-offer">
                <img className="logo-campana" src={carline.ofertasCampaign[0].logo.url} />
                <img src={carline.mediaAssets.D6MOFASidePC} alt={offer.carlineTitle} className="car-image" />
              </StyledImageWrapper>
            </div>

            <CarlineModelCuotaInfoContainer>
              <InfoContainer>
                <MotorizacionContainer>
                  <Text appearance={TokenTextAppearance.headline200}>{parse(carline.ofertasCampaign[0].titulo)}</Text>
                </MotorizacionContainer>

                <Container padding={{ top: ContainerPadding.static250 }} wrap={"never"}>
                  <Text appearance={TokenTextAppearance.copy200}>{parse(carline.ofertasCampaign[0].descripcion)}</Text>
                </Container>
              </InfoContainer>

              <div style={{ paddingTop: '32px' }} className="botton-wrapper">
                <div
                  onClick={() => {
                    trackCTAClick(
                      carline.ofertasCampaign[0].cta.label,
                      carline.name,
                      carline.ofertasCampaign[0].name,
                      carline.ofertasCampaign[0].cta.url
                    );
                  }}
                >
                  <CTA emphasis="primary" size="large" tag="a" href={carline.ofertasCampaign[0].cta.url}>
                    {carline.ofertasCampaign[0].cta.label}
                  </CTA>
                </div>
              </div>
            </CarlineModelCuotaInfoContainer>
          </Layout>
        </Container>
      </OfferCampaignContainer>
    </CSSTransition>
  );
};
